import { reactive } from 'vue';
import { useAppointmentsStore } from '../stores/useAppointmentsStore';
import type {
  Appointment,
  AppointmentRow,
} from '../stores/useAppointmentsStore';
import { useInvoicesStore } from '../stores/useInvoicesStore';
import { useQuotesStore } from '../stores/useQuotesStore';
import { useAuthStore } from '../stores/useAuthStore';
import type { QuoteRow } from '../api/services/interfaces/QuoteService';
import type { InvoiceRow } from '../api/services/interfaces/InvoiceService';
import {
  deriveBalance,
  deriveDashboardState,
  deriveHeroPillKey,
} from '../utils/dashboardAdapter';
import type { DashboardData, DashboardWidgetStatus } from '../types/dashboard';

/**
 * `useDashboard` — the home-dashboard orchestrator (Phase 06.1, DASH-01).
 *
 * Mirrors {@link useReportsFanOut} exactly (the `Promise.allSettled` fan-out
 * precedent), extended to FOUR sources + the hero per-id detail fetch + state
 * derivation through the Wave 1 pure adapter. It returns a single swappable
 * {@link DashboardData} interface (D-01) so a future `/v4/dashboard` aggregate
 * endpoint can drop in behind this shape without a view rewrite.
 *
 * Failure isolation (D-09/D-10 / T-06.1-04): the three `fetchReport()` actions
 * catch internally and DO NOT throw — they set their own `status`/`reportStatus`
 * ref to `'error'` and preserve last-known data. So per-widget status is read
 * from EACH STORE'S OWN ref, never the `allSettled` tuple — one widget's error
 * never blanks another. The only fetch that throws is the hero's per-id
 * `fetchAppointment(id)`; it is wrapped in its OWN try/catch driving a
 * hero-only `heroStatus`, so a hero detail failure never touches balance.
 *
 * Refetch (D-03): `fetchAll()` runs the report fetches unconditionally on every
 * call — it never short-circuits on pre-existing data. The view calls it on
 * mount; `useResetUserScopedStores` clears stores on logout/expiry so an
 * account switch never shows stale prior-account data (T-06.1-05).
 *
 * Greeting (B-3 / P-4): read read-only from `useAuthStore().user`. NO
 * customer-self-fetch fallback — that endpoint does not exist (B-3). Read
 * defensively so a
 * thin user (before `restoreSession` completes) yields an empty-but-renderable
 * greeting and `isMember` only flips true when `user.membership` is truthy
 * (T-06.1-06).
 */

// "Active" statuses mirror useAppointmentsStore.ACTIVE_STATUSES (the same set
// used for the nav badge + the appointments active/history split). The report
// sorts desc by date, so the FIRST active-status row is the most-recent active
// row — the dashboard hero pick (RESEARCH Assumption A2).
const ACTIVE_STATUSES = new Set([
  'pending',
  'assigned',
  'accepted',
  'running',
  'late',
  'preferred_assignment',
]);

// Pick the hero from the active rows, prioritizing an ARRIVED appointment when
// several active rows of different statuses are present. On a lean report row
// (no dispatch_time / arrival_time) the only arrived signal is
// `status === 'running'`: per the API invariant a running appointment always
// carries arrival_time, so on a timestamp-less datatable row `running` IS the
// arrived dispatch state (memory project_running_status_means_arrived). En route
// — `accepted` + dispatch_time — is indistinguishable from plain `accepted`
// here, so arrived is the only tier we can prioritize at pick-time. Within each
// tier the report's desc-by-date sort keeps the most-recent first.
function pickUpcomingActiveRow(rows: AppointmentRow[]): AppointmentRow | null {
  if (!Array.isArray(rows)) return null;
  const active = rows.filter((row) => ACTIVE_STATUSES.has(row.status));
  if (active.length === 0) return null;
  return active.find((row) => row.status === 'running') ?? active[0];
}

function buildGreeting(firstName: string | undefined): string {
  const name = (firstName ?? '').trim();
  return name ? `Welcome back, ${name}` : 'Welcome back';
}

interface HeroResult {
  appointment: Appointment | null;
  pillKey: ReturnType<typeof deriveHeroPillKey> | null;
  status: DashboardWidgetStatus;
}

/**
 * Resolve the hero from a report-pick + per-id detail fetch in its OWN
 * try/catch (B-2 / D-10). A null row → idle (NOT error); a thrown detail fetch
 * → hero-only error. Never touches any other widget.
 */
async function resolveHero(
  heroRow: AppointmentRow | null,
  fetchAppointment: (id: number) => Promise<Appointment>,
): Promise<HeroResult> {
  if (!heroRow) {
    return { appointment: null, pillKey: null, status: 'idle' };
  }
  try {
    const appt = await fetchAppointment(heroRow.id);
    const pillKey = deriveHeroPillKey({
      status: appt.status,
      dispatch_time: appt.dispatch_time ?? null,
      arrival_time: appt.arrival_time ?? null,
    });
    return { appointment: appt, pillKey, status: 'success' };
  } catch {
    return { appointment: null, pillKey: null, status: 'error' };
  }
}

/**
 * Pending-quote widget projection (dashboard-home.md §5). The dashboard card is
 * intentionally PENDING-ONLY: only a genuinely pending, actionable quote is the
 * card's `latest`. Denied rows are counted by badgeCount at the nav surface
 * (D-06), never surfaced here; §5's pending/denied render parity is a
 * PendingQuoteCard concern, not a selection concern. Now that adaptQuoteRow
 * (Plan 09) maps each wire status explicitly, a historical accepted/expired/
 * closed quote never reaches `status === 'pending'`, so `latest` is null for a
 * no-activity account (the quote-half of UAT Test 12).
 */
function projectQuote(
  rows: QuoteRow[],
  pendingCount: number,
): { pendingCount: number; latest: QuoteRow | null } {
  const latest = rows.find((row) => row.status === 'pending') ?? null;
  return { pendingCount, latest };
}

/**
 * Settled gate (Plan 08): the state derivation may only run once every
 * per-widget source it consults has reached a terminal status. The three
 * report statuses must be 'success' | 'error' (NOT 'idle' / 'loading').
 * heroStatus is the nuance: it is legitimately 'idle' when there is no active
 * appointment — that is a SETTLED outcome, not a pending one — so only an
 * in-flight hero detail fetch ('loading') keeps us un-settled.
 */
function isSettled(
  invoicesStatus: DashboardWidgetStatus,
  appointmentsStatus: DashboardWidgetStatus,
  quotesStatus: DashboardWidgetStatus,
  heroStatus: DashboardWidgetStatus,
): boolean {
  const reportSettled = (s: DashboardWidgetStatus): boolean =>
    s === 'success' || s === 'error';
  return (
    reportSettled(invoicesStatus) &&
    reportSettled(appointmentsStatus) &&
    reportSettled(quotesStatus) &&
    heroStatus !== 'loading'
  );
}

/** D-03/first-time split: any past activity across the three report sources. */
function hasAnyHistory(
  appts: AppointmentRow[],
  invoices: InvoiceRow[],
  quotes: QuoteRow[],
): boolean {
  return appts.length > 0 || invoices.length > 0 || quotes.length > 0;
}

// eslint-disable-next-line max-lines-per-function -- the composable setup aggregates the four-source fan-out, per-widget status reads, hero detail fetch, and state derivation into one cohesive orchestrator (mirrors the store-setup pattern); splitting fetchAll out of the closure would lose the stable reactive `data` reference the DashboardData interface returns.
export function useDashboard(): DashboardData<
  Appointment,
  QuoteRow,
  AppointmentRow
> {
  const appointmentsStore = useAppointmentsStore();
  const invoicesStore = useInvoicesStore();
  const quotesStore = useQuotesStore();
  const authStore = useAuthStore();

  // Reactive, mutable view model. We return a single reactive object so the
  // view can bind to a stable reference whose fields update after each
  // fetchAll() — the swappable DashboardData interface (D-01).
  const data = reactive({
    fetchAll,

    // Non-terminal sentinel until the first fetchAll settles (Plan 08). The
    // empty pre-fetch snapshot must NOT be read as a terminal first-time/
    // no-activity account during the load window; the view maps 'loading' to
    // the per-card skeletons.
    state: 'loading',
    balance: deriveBalance([]),
    greeting: buildGreeting(authStore.user?.first_name),
    isMember: Boolean(authStore.user?.membership),

    hero: { appointment: null, pillKey: null },
    quote: { pendingCount: 0, latest: null },
    recentVisits: [],

    invoicesStatus: 'idle' as DashboardWidgetStatus,
    appointmentsStatus: 'idle' as DashboardWidgetStatus,
    quotesStatus: 'idle' as DashboardWidgetStatus,
    heroStatus: 'idle' as DashboardWidgetStatus,
  }) as DashboardData<Appointment, QuoteRow, AppointmentRow>;

  async function fetchAll(): Promise<void> {
    // Re-enter the loading window on EVERY call (CR-01): set the page state +
    // per-widget statuses to the non-terminal sentinels synchronously, before
    // awaiting, so a refetch/retry re-shows the per-card skeletons (D-11)
    // instead of painting stale terminal content until the new data settles.
    // These are overwritten with the stores' real statuses after allSettled.
    data.state = 'loading';
    data.invoicesStatus = 'loading';
    data.appointmentsStatus = 'loading';
    data.quotesStatus = 'loading';
    data.heroStatus = 'loading';

    // D-09: fan out over the three report fetches. They swallow their own
    // errors, so allSettled is a defensive guard, not the error source.
    await Promise.allSettled([
      invoicesStore.fetchReport(),
      appointmentsStore.fetchReport(),
      quotesStore.fetchReport(),
    ]);

    // D-10: per-widget status comes from each store's OWN status ref.
    data.invoicesStatus = invoicesStore.status;
    data.appointmentsStatus = appointmentsStore.reportStatus;
    data.quotesStatus = quotesStore.status;

    // Greeting + membership: read-only auth (B-3 — no self-fetch fallback).
    data.greeting = buildGreeting(authStore.user?.first_name);
    data.isMember = Boolean(authStore.user?.membership);

    // Balance split (D-02 SPB-only past-due via the pure adapter).
    data.balance = deriveBalance(invoicesStore.report);
    data.quote = projectQuote(quotesStore.report, quotesStore.pendingCount);
    // Recent visits: the 3 most-recent rows (report is desc by date already).
    // dashboard-home.md §6/§10 — the dashboard surfaces only the last 3.
    data.recentVisits = appointmentsStore.report.slice(0, 3);

    // Hero: report-pick → per-id detail fetch in its OWN try/catch (B-2/D-10).
    const heroRow = pickUpcomingActiveRow(appointmentsStore.report);
    const hero = await resolveHero(heroRow, appointmentsStore.fetchAppointment);
    data.hero = { appointment: hero.appointment, pillKey: hero.pillKey };
    data.heroStatus = hero.status;

    // Settled gate (Plan 08) — see isSettled(). Because fetchAll awaits
    // allSettled + the hero detail before deriving, `settled` is normally true
    // here; the 'loading' sentinel is what the INITIAL reactive value carries
    // until this first fetchAll completes.
    const settled = isSettled(
      data.invoicesStatus,
      data.appointmentsStatus,
      data.quotesStatus,
      data.heroStatus,
    );

    // Top-level state from the pure adapter. The active hero row drives the
    // en-route/arrived states; otherwise balance/history decide.
    data.state = deriveDashboardState({
      loading: !settled,
      activeAppointment: heroRow
        ? {
            status: heroRow.status,
            dispatch_time: hero.appointment?.dispatch_time ?? null,
            arrival_time: hero.appointment?.arrival_time ?? null,
          }
        : null,
      hasHistory: hasAnyHistory(
        appointmentsStore.report,
        invoicesStore.report,
        quotesStore.report,
      ),
      balance: data.balance,
    });
  }

  return data;
}
